var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Box, Button, Card, CardContent, Chip, Divider, Grid, IconButton, Paper, Tooltip, Typography, } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import React from "react";
import { Link } from "react-router-dom";
import { useCancelInvitation, useGenerateInviteLink, useListTeamInvitations, useRetrieveTeamInviteLink, useRevokeInviteLink, } from "../api/InvitationApiProvider";
import { useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ROUTES } from "../breadcrumbs/routeConfig";
import { EmptyState } from "../common/EmptyState";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { DeleteDialog } from "../utils/DeleteDialog";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import SuccessSnackbar from "../utils/SuccessSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export var TeamInvitations = function () {
    var teamId = useNumericParams().id;
    var _a = useRetrieveUserTeamPermissions({ teamId: teamId }), permissions = _a.data, isPermLoading = _a.isLoading;
    var canModify = (permissions === null || permissions === void 0 ? void 0 : permissions.canModify) === true;
    var _b = useRetrieveTeam({ id: teamId, enabled: canModify }), team = _b.data, isTeamLoading = _b.isLoading, teamError = _b.error;
    var _c = useListTeamInvitations({ teamId: teamId, enabled: canModify }), invitations = _c.data, isInvLoading = _c.isLoading, invError = _c.error;
    var _d = __read(React.useState(""), 2), successMessage = _d[0], setSuccessMessage = _d[1];
    var _e = __read(React.useState(""), 2), errorMessage = _e[0], setErrorMessage = _e[1];
    if (isPermLoading || (canModify && (isTeamLoading || isInvLoading))) {
        return _jsx(Loading, {});
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (teamError || invError || !team) {
        return _jsx(ErrorView, { error: teamError || invError });
    }
    return (_jsxs(React.Fragment, { children: [_jsxs(Grid, { container: true, spacing: 2, alignItems: "center", children: [_jsx(Grid, { item: true, xs: true, children: _jsxs(Typography, { variant: "h4", gutterBottom: true, children: ["Invitations \u2014 ", team.name] }) }), _jsx(Grid, { item: true, children: _jsx(Link, { to: ROUTES.TEAM_INVITATIONS_SEND.replace(":id", String(teamId)), style: { textDecoration: "none" }, children: _jsx(Button, { variant: "contained", color: "primary", startIcon: _jsx(PersonAddIcon, {}), children: "Send Invitation" }) }) })] }), _jsx(Divider, { sx: { my: 2 } }), _jsx(InviteLinkSection, { teamId: teamId, onSuccess: setSuccessMessage, onError: setErrorMessage }), _jsx(Typography, { variant: "h6", sx: { mt: 4, mb: 2 }, children: "Pending Invitations" }), invitations && invitations.length > 0 ? (_jsx(Grid, { container: true, spacing: 2, children: invitations.map(function (inv) { return (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, children: _jsx(PendingInvitationCard, { invitation: inv, teamId: teamId, onSuccess: setSuccessMessage, onError: setErrorMessage }) }, inv.id)); }) })) : (_jsx(EmptyState, { icon: _jsx(MailOutlineIcon, {}), heading: "No pending invitations", description: "Send an invitation to a player to get started." })), _jsx(SuccessSnackbar, { open: !!successMessage, onClose: function () { return setSuccessMessage(""); }, message: successMessage }), _jsx(ErrorSnackbar, { open: !!errorMessage, onClose: function () { return setErrorMessage(""); }, message: errorMessage })] }));
};
var InviteLinkSection = function (_a) {
    var _b;
    var teamId = _a.teamId, onSuccess = _a.onSuccess, onError = _a.onError;
    var existingLink = useRetrieveTeamInviteLink({ teamId: teamId }).data;
    var _c = __read(React.useState(null), 2), generatedLink = _c[0], setGeneratedLink = _c[1];
    var _d = __read(React.useState(false), 2), copied = _d[0], setCopied = _d[1];
    var linkData = (_b = generatedLink !== null && generatedLink !== void 0 ? generatedLink : existingLink) !== null && _b !== void 0 ? _b : null;
    var generateMutation = useGenerateInviteLink({
        teamId: teamId,
        onSuccess: function (response) {
            setGeneratedLink(response.data);
            onSuccess("Invite link generated!");
        },
        onError: function () { return onError("Failed to generate invite link."); },
    });
    var revokeMutation = useRevokeInviteLink({
        teamId: teamId,
        onSuccess: function () {
            setGeneratedLink(null);
            onSuccess("Invite link revoked.");
        },
        onError: function () { return onError("Failed to revoke invite link."); },
    });
    var joinUrl = linkData
        ? "".concat(window.location.origin, "/join/team/").concat(linkData.token)
        : "";
    var handleCopy = function () {
        navigator.clipboard.writeText(joinUrl);
        setCopied(true);
        setTimeout(function () { return setCopied(false); }, 2000);
    };
    return (_jsxs(Paper, { variant: "outlined", sx: { p: 2 }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(LinkIcon, { color: "primary" }), _jsx(Typography, { variant: "h6", children: "Invite Link" })] }), !linkData ? (_jsx(Button, { variant: "outlined", startIcon: _jsx(LinkIcon, {}), disabled: generateMutation.isLoading, onClick: function () { return generateMutation.mutate(); }, children: generateMutation.isLoading ? "Generating..." : "Generate Link" })) : (_jsx(Button, { variant: "outlined", color: "error", startIcon: _jsx(LinkOffIcon, {}), disabled: revokeMutation.isLoading, onClick: function () { return revokeMutation.mutate(); }, children: revokeMutation.isLoading ? "Revoking..." : "Revoke Link" }))] }), linkData && (_jsxs(Box, { sx: {
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "grey.50",
                    p: 1.5,
                    borderRadius: 1,
                }, children: [_jsx(Typography, { variant: "body2", sx: {
                            flexGrow: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, children: joinUrl }), _jsx(Tooltip, { title: copied ? "Copied!" : "Copy link", children: _jsx(IconButton, { size: "small", onClick: handleCopy, children: _jsx(ContentCopyIcon, { fontSize: "small" }) }) })] })), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 1 }, children: "Anyone with the link can join this team. Links expire after 30 days." })] }));
};
var PendingInvitationCard = function (_a) {
    var invitation = _a.invitation, teamId = _a.teamId, onSuccess = _a.onSuccess, onError = _a.onError;
    var _b = __read(React.useState(false), 2), showCancel = _b[0], setShowCancel = _b[1];
    var cancelMutation = useCancelInvitation({
        onMutate: function () { },
        onSuccess: function () {
            setShowCancel(false);
            onSuccess("Invitation cancelled.");
        },
        onError: function () {
            setShowCancel(false);
            onError("Failed to cancel invitation.");
        },
    });
    var inviteeName = invitation.invitee
        ? "".concat(invitation.inviteeFirstName, " ").concat(invitation.inviteeLastName)
        : invitation.email || "Unknown";
    return (_jsxs(_Fragment, { children: [_jsx(Card, { variant: "outlined", children: _jsxs(CardContent, { children: [_jsxs(Box, { sx: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                            }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", fontWeight: "bold", children: inviteeName }), invitation.email && !invitation.invitee && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: invitation.email }))] }), _jsx(Tooltip, { title: "Cancel invitation", children: _jsx(IconButton, { size: "small", color: "error", onClick: function () { return setShowCancel(true); }, children: _jsx(DeleteIcon, { fontSize: "small" }) }) })] }), invitation.mergePlayer && (_jsx(Chip, { label: "Player merge", size: "small", color: "info", sx: { mt: 1 } })), _jsxs(Typography, { variant: "caption", color: "text.secondary", sx: { display: "block", mt: 1 }, children: ["Sent", " ", formatDistanceToNow(new Date(invitation.createdAt), {
                                    addSuffix: true,
                                })] })] }) }), _jsx(DeleteDialog, { open: showCancel, title: "Cancel Invitation", description: "Are you sure you want to cancel the invitation to ".concat(inviteeName, "?"), onCancel: function () { return setShowCancel(false); }, onConfirm: function () {
                    return cancelMutation.mutate({ invitationId: invitation.id, teamId: teamId });
                }, confirmText: "Cancel Invitation", confirmColor: "error" })] }));
};
